import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
} from "recharts";
import { CalendarCheck, Clock, ArrowRight, Sparkles } from "lucide-react";

function iso(d) {
    return d.toISOString().slice(0, 10);
}

function fmtShort(d) {
    const dt = new Date(`${d}T00:00:00Z`);
    return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}`;
}

function fmtMins(secs) {
    return Math.round((secs || 0) / 60);
}

/**
 * Compact "last 7 days" attendance card for the student dashboard.
 * Defaults to the first enrollment; shows 0-state if not enrolled.
 */
export default function MyAttendanceCard({ enrollments, onStreakChange }) {
    const programOptions = useMemo(
        () =>
            (enrollments || [])
                .filter((e) => e?.program?.id)
                .map((e) => ({ id: e.program.id, title: e.program.title })),
        [enrollments],
    );

    const [programId, setProgramId] = useState(programOptions[0]?.id || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!programOptions.length) return;
        if (!programOptions.find((p) => p.id === programId)) {
            setProgramId(programOptions[0].id);
        }
    }, [programOptions, programId]);

    useEffect(() => {
        if (!programId) return;
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 6);
        setLoading(true);
        api.get("/me/attendance", {
            params: { program_id: programId, start: iso(start), end: iso(end) },
        })
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [programId]);

    const chartData = useMemo(() => {
        if (!data) return [];
        return data.days.map((day) => {
            const cell = data.row.cells?.[day] || {};
            return {
                day: fmtShort(day),
                minutes: fmtMins(cell.active_seconds),
                present: cell.status === "present",
            };
        });
    }, [data]);

    // Trailing-days streak — count consecutive days ending today where the
    // student was "present" (>= 10 min studied).
    useEffect(() => {
        if (!onStreakChange || !chartData.length) return;
        let streak = 0;
        for (let i = chartData.length - 1; i >= 0; i--) {
            if (chartData[i].present) streak += 1;
            else break;
        }
        onStreakChange(streak);
    }, [chartData, onStreakChange]);

    const totals = data?.row?.totals || { present: 0, absent: 0, active_seconds: 0 };

    if (!programOptions.length) {
        return (
            <div
                className="h-full rounded-2xl bg-card border border-primary/10 p-6 flex flex-col items-center justify-center text-center min-h-[280px]"
                data-testid="my-attendance-card-empty"
            >
                <div className="h-12 w-12 rounded-full bg-primary/8 text-primary flex items-center justify-center mb-3">
                    <CalendarCheck className="h-5 w-5" />
                </div>
                <p className="font-display text-base text-foreground">
                    Attendance starts when you do
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                    Enrol in a program — your study time will be tracked here.
                </p>
            </div>
        );
    }

    return (
        <div
            className="h-full rounded-2xl bg-card border border-primary/10 p-6"
            data-testid="my-attendance-card"
        >
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                            <CalendarCheck className="h-5 w-5" />
                        </div>
                        <div>
                            <h3 className="font-display text-lg text-foreground tracking-tight leading-none">
                                My Attendance · last 7 days
                            </h3>
                            <p className="text-xs text-muted-foreground mt-1">
                                10+ minutes of focused study counts as present.
                            </p>
                        </div>
                    </div>
                    <Button
                        asChild
                        size="sm"
                        variant="ghost"
                        className="h-8 gap-1 text-primary"
                        data-testid="my-attendance-card-view-all"
                    >
                        <Link to="/me/attendance">
                            Full view
                            <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                    </Button>
                </div>

                {programOptions.length > 1 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                        {programOptions.map((p) => {
                            const active = p.id === programId;
                            return (
                                <button
                                    type="button"
                                    key={p.id}
                                    onClick={() => setProgramId(p.id)}
                                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                                        active
                                            ? "bg-primary/10 border-primary/40 text-primary"
                                            : "bg-muted/40 border-border text-muted-foreground hover:bg-muted/70"
                                    }`}
                                    data-testid={`my-attendance-program-${p.id}`}
                                >
                                    {p.title.length > 24
                                        ? `${p.title.slice(0, 23)}…`
                                        : p.title}
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className="grid grid-cols-3 gap-3 mb-4">
                    <KpiMini
                        label="Present"
                        value={totals.present}
                        suffix="days"
                        accent="primary"
                    />
                    <KpiMini
                        label="Absent"
                        value={totals.absent}
                        suffix="days"
                        accent="muted"
                    />
                    <KpiMini
                        label="Studied"
                        value={Math.round(totals.active_seconds / 60)}
                        suffix="min"
                        accent="accent"
                    />
                </div>

                <div className="h-32 -mx-2">
                    {loading ? (
                        <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                            <Clock className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                            Loading…
                        </div>
                    ) : chartData.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                            No data yet
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <XAxis
                                    dataKey="day"
                                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis hide />
                                <Tooltip
                                    cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
                                    contentStyle={{
                                        background: "hsl(var(--card))",
                                        border: "1px solid hsl(var(--border))",
                                        borderRadius: 6,
                                        fontSize: 11,
                                    }}
                                    formatter={(v) => [`${v} min`, "Studied"]}
                                />
                                <Bar
                                    dataKey="minutes"
                                    radius={[4, 4, 0, 0]}
                                    fill="hsl(var(--primary))"
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>

                <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="w-full mt-3 gap-1.5"
                    data-testid="my-attendance-card-cta"
                >
                    <Link to="/me/attendance">
                        <Sparkles className="h-3.5 w-3.5" />
                        View full attendance history
                    </Link>
                </Button>
        </div>
    );
}

function KpiMini({ label, value, suffix, accent }) {
    const tone =
        accent === "primary"
            ? "text-primary"
            : accent === "accent"
              ? "text-accent-foreground"
              : "text-foreground";
    return (
        <div className="rounded-md bg-muted/30 border border-border p-2.5">
            <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                {label}
            </div>
            <div className={`font-display text-xl ${tone} mt-0.5 leading-none`}>
                {value}
                {suffix && (
                    <span className="text-[10px] text-muted-foreground ml-1 font-sans">
                        {suffix}
                    </span>
                )}
            </div>
        </div>
    );
}
